import { User } from "../domain/User"
import { Logger } from "../shared/Logger"
import { delayMs } from "../shared/util"
import { getAuth } from "./Connections/connectionState"
import { AppRequestOptions } from "./Connections/httpsWrappers"

export type TextMode = 'text' | 'markdown'

/**
 * use cases
 *  get default list of threads
 *      query threads by page by last post date
 *  search for threads by title name
 *      scan like in default list, for threads with title matching search string
 *      allow people to add summary text / tags?
 *  search for threads by tags
 *      when writing a thread, also write a tag-date
 *      update tag-dates when writing a post
 *      events should be tagged with yyyymmdd ?
 *  search for threads by text
 *      tougher - probably need to feed to opensearch or something
 *  TODO search for threads by event date
 *  
 *  get thread, latest to newest
 *  get thread, all data
 * 
 *  what if we wanted to be local first?
 *      allow polling since activity
 *  get thread, since date
 * 
 *  threads / posts - can we scan reverse order? 
 *  pk - uuid
 *  sk - thread uses ##thread, posts use #postdate#(post date)#uuid
 *  taglist - tag1,tag2,tag3 
 * 
 *  GSI1pk - #threadDateTypeTitleTags
 *  GSIsk - postdate#(last post date)#type#(threadtype)#title#(title)#tags#(tag,tag2,tag3)
 * 
 * 
 *  thread tags - match these first to get list of thread tags
 *  pk - THREADTAGS#
 *  sk - tag
 *  later, in config for now
 * 
 *  
 *  thread tag usage
 *  pk - THREADTAGS#tag
 *  sk - lastpostdate#threadId
 *  title
 *  last post date
 *  author handle
 *  author id
 *  thread type
 *  thread create date
 *  
 *  on thread create, add to THREADTAGS#tag for each tag
 *  on post create, update last post date / threadid for each tag
 *    gsi1 will include searchability data
 */

export type GetThreadsParam = {
    searchText?: string
    count?: number
}

type GetThreadsOptions = {
    count: number
}

export type GetThreadParam = {
    threadId: string
    count?: number
}

export type PostThreadParam = {
    threadId: string
    body: string
}

type GetThreadOptions = {
    threadId: string
    count: number
}

export type ThreadType = 'discussion' | 'event' | 'recipe'

export interface ThreadSummary {
    id: string
    title: string
    threadType: ThreadType
    tags: string[]
    createDate: Date,
    lastPostDate?: Date,
    authorId: string,
    authorHandle: string
}

export interface ThreadData extends ThreadSummary {
    topicPost: string
    postList: PostList
}

export interface PostList {
    posts: Post[],
    start: number,
    end: number
}

export interface Post {
    postId: string,
    threadId: string,
    authorId: string,
    authorHandle: string,
    postDate: Date,
    body: string,
    textMode?: TextMode
}

const MAX_THREAD_REQUEST = 100
const DEFAULT_THREAD_REQUEST = 5
const MAX_POST_REQUEST = 100

const sampleThreads: ThreadData[] = [
    {
        id: '080a67e7-74ff-4e5d-8e73-778f51f3d631',
        title: 'is this thing on?',
        threadType: 'discussion',
        tags: ['art','philosophy'],
        topicPost: 'i\'m just not sure one way or the other',
        createDate: new Date(2025,11,23),
        authorId: '080a67e7-74ff-4e5d-8e73-778f51f3d631',
        authorHandle: 'mr yes',
        postList: {
            start: 0,
            end: 3,
            posts: [
                {
                    authorId: '080a67e7-74ff-4e5d-8e73-778f51f3d631',
                    authorHandle: 'mr yes',
                    body: 'yes',
                    postDate: new Date(),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: 'd6cf86a6-49f4-4c52-aa72-b245cfc6e707',
                    authorHandle: 'mr no',
                    body: 'no',
                    postDate: new Date(),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '080a67e7-74ff-4e5d-8e73-778f51f3d631',
                    authorHandle: 'mr yes',
                    body: 'what do you mean??',
                    postDate: new Date(),
                    postId: "",
                    threadId: ""
                },
            ]
        }
    },
    {
        id: '080a67e7-74ff-4e5d-8e73-778f51f3d631',
        title: 'is this thing on yet?',
        threadType: 'discussion',
        tags: ['art','philosophy'],
        topicPost: 'i\'m just not sure one way or the other',
        createDate: new Date(2025,11,22),
        authorId: '080a67e7-74ff-4e5d-8e73-778f51f3d631',
        authorHandle: 'mr yes',
        postList: {
            start: 0,
            end: 3,
            posts: [
                {
                    authorId: '080a67e7-74ff-4e5d-8e73-778f51f3d631',
                    authorHandle: 'mr yes',
                    body: 'yes',
                    postDate: new Date(),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: 'd6cf86a6-49f4-4c52-aa72-b245cfc6e707',
                    authorHandle: 'mr no',
                    body: 'no',
                    postDate: new Date(),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '080a67e7-74ff-4e5d-8e73-778f51f3d631',
                    authorHandle: 'mr yes',
                    body: 'what do you mean??',
                    postDate: new Date(),
                    postId: "",
                    threadId: ""
                },
            ]
        }
    },
    {
        id: 'c3c1b5b9-4e4f-4b21-8e1f-1b4a2b99f310',
        title: 'favorite recent noise sets in the cities?',
        threadType: 'discussion',
        tags: ['music','noise','local'],
        topicPost: 'AI generated sample text.  anyone catch that basement noise triple-bill in longfellow last night? my ears are still politely screaming.',
        createDate: new Date(2025,1,14,18,32),
        authorId: '19a6ce19-0f48-4cf6-982c-55a214e8df61',
        authorHandle: 'river_aster',
        postList: {
            start: 0,
            end: 10,
            posts: [
                {
                    authorId: '19a6ce19-0f48-4cf6-982c-55a214e8df61',
                    authorHandle: 'river_aster',
                    body: 'opening act sounded like a dishwasher being exorcised and i mean that as the highest compliment.',
                    postDate: new Date(2025, 1, 14, 18, 35),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '7bd4e41c-3375-4e1b-8ede-f5cb252df240',
                    authorHandle: 'trash_harp',
                    body: `i was there! the strobes were brutal but the contact-mic-on-metal-bowl thing? chef's kiss.`,
                    postDate: new Date(2025, 1, 14, 18, 41),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '0c6db928-dbbe-4b45-9f1f-6c0cca0a4aa3',
                    authorHandle: 'mossmoth',
                    body: `couldn't make it, was at the midtown greenhouse set. vines + modular drones = transcendence.`,
                    postDate: new Date(2025, 1, 14, 19, 2),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '5c428e81-d44b-4359-9a9d-3f3c8a8b9c3e',
                    authorHandle: 'redflag_rye',
                    body: `longfellow basement squad is undefeated. also whoever brought the cauldron (??) for percussion deserves a medal.`,
                    postDate: new Date(2025, 1, 14, 19, 20),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '7bd4e41c-3375-4e1b-8ede-f5cb252df240',
                    authorHandle: 'trash_harp',
                    body: 'it WAS a cauldron, i asked. they said “witchy but practical” which seems like a life philosophy honestly.',
                    postDate: new Date(2025, 1, 14, 19, 25),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '19a6ce19-0f48-4cf6-982c-55a214e8df61',
                    authorHandle: 'river_aster',
                    body: 'lol i love our scene. every show is 30% art school, 30% coven, 30% communist potluck energy.',
                    postDate: new Date(2025, 1, 14, 19, 31),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '0c6db928-dbbe-4b45-9f1f-6c0cca0a4aa3',
                    authorHandle: 'mossmoth',
                    body: `speaking of potlucks: anyone going to the noise brunch at moon palace on sunday? i'll bring muffins.`,
                    postDate: new Date(2025, 1, 14, 20, 4),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '5c428e81-d44b-4359-9a9d-3f3c8a8b9c3e',
                    authorHandle: 'redflag_rye',
                    body: 'planning to! hoping someone does a set entirely with broken keyboards again. that ruled last time.',
                    postDate: new Date(2025, 1, 14, 20, 17),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '7bd4e41c-3375-4e1b-8ede-f5cb252df240',
                    authorHandle: 'trash_harp',
                    body: 'i can loan out two thrift-store casios if anyone wants to get weird.',
                    postDate: new Date(2025, 1, 14, 20, 25),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '19a6ce19-0f48-4cf6-982c-55a214e8df61',
                    authorHandle: 'river_aster',
                    body: `ok now i'm definitely going. nothing like brunch with friends and dissonance.`,
                    postDate: new Date(2025, 1, 14, 20, 29),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '3f2c52b0-1c1a-4f4e-a3f0-0a5f2cbe88b0',
                    authorHandle: 'ghost_crayon',
                    body: `hi i'm new here—was anyone at that riverside warehouse thing where a dude was literally fishing broken glass out of the trash to scrape over a homemade mic???`,
                    postDate: new Date(2025, 1, 14, 20, 41),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '3f2c52b0-1c1a-4f4e-a3f0-0a5f2cbe88b0',
                    authorHandle: 'ghost_crayon',
                    body: 'i swear it sounded like a raccoon doing foley work for a horror movie. incredible but also so bad. like spiritually bad.',
                    postDate: new Date(2025, 1, 14, 20, 42),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '3f2c52b0-1c1a-4f4e-a3f0-0a5f2cbe88b0',
                    authorHandle: 'ghost_crayon',
                    body: `also i met someone there and they were super sweet but i was a little too drunk and cannot remember their name at all. i think it started with M? or maybe B? i'm mortified lol.`,
                    postDate: new Date(2025, 1, 14, 20, 46),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '7bd4e41c-3375-4e1b-8ede-f5cb252df240',
                    authorHandle: 'trash_harp',
                    body: `LMAO i know exactly who you mean—was the glass guy wearing a reflective safety vest? that's glint_lord. he's a menace.`,
                    postDate: new Date(2025, 1, 14, 20, 52),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '5c428e81-d44b-4359-9a9d-3f3c8a8b9c3e',
                    authorHandle: 'redflag_rye',
                    body: 'oh noooooo the drunk-name-forgetting curse. i once introduced someone as “my friend whose name i will remember in five minutes” and then never remembered.',
                    postDate: new Date(2025, 1, 14, 20, 55),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '0c6db928-dbbe-4b45-9f1f-6c0cca0a4aa3',
                    authorHandle: 'mossmoth',
                    body: 'same, i had a whole conversation with someone about their cat and then found out 2 months later their name was not “dandelion guy.”',
                    postDate: new Date(2025, 1, 14, 21, 1),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '19a6ce19-0f48-4cf6-982c-55a214e8df61',
                    authorHandle: 'river_aster',
                    body: `tbh half the twin cities scene could just wear name tags that say “sorry i forgot already” and it'd save us all time.`,
                    postDate: new Date(2025, 1, 14, 21, 4),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '3f2c52b0-1c1a-4f4e-a3f0-0a5f2cbe88b0',
                    authorHandle: 'ghost_crayon',
                    body: `“glint_lord” omg. that fits too well. at one point he bowed the glass like it was a violin and i thought the amp was going to cry.`,
                    postDate: new Date(2025, 1, 14, 21, 10),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '7bd4e41c-3375-4e1b-8ede-f5cb252df240',
                    authorHandle: 'trash_harp',
                    body: `the amp DID cry. that feedback spiral? that was the sound of regret.`,
                    postDate: new Date(2025, 1, 14, 21, 12),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '5c428e81-d44b-4359-9a9d-3f3c8a8b9c3e',
                    authorHandle: 'redflag_rye',
                    body: `i once watched someone mic up a busted ceramic pot. the entire set was just crunchy pottery heartbreak.`,
                    postDate: new Date(2025, 1, 14, 21, 18),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '19a6ce19-0f48-4cf6-982c-55a214e8df61',
                    authorHandle: 'river_aster',
                    body: `that actually sounds beautiful in a “my soul lives in a thrift store” kinda way.`,
                    postDate: new Date(2025, 1, 14, 21, 20),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '3f2c52b0-1c1a-4f4e-a3f0-0a5f2cbe88b0',
                    authorHandle: 'ghost_crayon',
                    body: 'this board is making me feel so much better about the name thing lol. community amnesia is apparently normal.',
                    postDate: new Date(2025, 1, 14, 21, 25),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '0c6db928-dbbe-4b45-9f1f-6c0cca0a4aa3',
                    authorHandle: 'mossmoth',
                    body: `seriously—half the time i remember people by their gear. “oh yeah you're the reel-to-reel wizard.”`,
                    postDate: new Date(2025, 1, 14, 21, 30),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '7bd4e41c-3375-4e1b-8ede-f5cb252df240',
                    authorHandle: 'trash_harp',
                    body: 'same. someone once recognized me only as “that person with the angry harp.” fair.',
                    postDate: new Date(2025, 1, 14, 21, 33),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '19a6ce19-0f48-4cf6-982c-55a214e8df61',
                    authorHandle: 'river_aster',
                    body: 'angry harp is iconic tbh.',
                    postDate: new Date(2025, 1, 14, 21, 35),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '5c428e81-d44b-4359-9a9d-3f3c8a8b9c3e',
                    authorHandle: 'redflag_rye',
                    body: `i once forgot someone's name so thoroughly i introduced them to someone else *by describing their jacket*. it worked, astonishingly.`,
                    postDate: new Date(2025, 1, 14, 21, 41),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '3f2c52b0-1c1a-4f4e-a3f0-0a5f2cbe88b0',
                    authorHandle: 'ghost_crayon',
                    body: `this is reassuring. also i'm now picturing glint_lord storing glass shards like pokemon.`,
                    postDate: new Date(2025, 1, 14, 21, 44),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '7bd4e41c-3375-4e1b-8ede-f5cb252df240',
                    authorHandle: 'trash_harp',
                    body: 'do NOT give him ideas.',
                    postDate: new Date(2025, 1, 14, 21, 45),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '0c6db928-dbbe-4b45-9f1f-6c0cca0a4aa3',
                    authorHandle: 'mossmoth',
                    body: 'too late, he definitely already has a system.',
                    postDate: new Date(2025, 1, 14, 21, 47),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '19a6ce19-0f48-4cf6-982c-55a214e8df61',
                    authorHandle: 'river_aster',
                    body: 'okay now i want to book a “found object showdown” night. bring your weirdest noise-making thing.',
                    postDate: new Date(2025, 1, 14, 21, 55),
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: 'a1b4d2f0-62c3-42e0-9c77-9df0ebc441aa',
                    authorHandle: 'glint_lord',
                    body: `hey. someone said Javascript javascript my name in here a while back so i figured i'd finally crawl out of ****whatever dimension**** i was in and contribute.[some text](javascript:alert('xss'))\n\n\n### okay. glass.\npeople think it's all the same but no—different shards have [different personalities](https://google.com). the thick-bottom bottle chunks? those give you that deep, resonant “doom scrape,” kinda like a glacier clearing its throat. ![glacier pic!!](https://nsidc.org/sites/default/files/styles/article_image_large/public/images/Exploring%20the%20Perito%20Moreno%20Glacier_Bruce%20Raup_2019-12-19-33_6329.jpg.webp?itok=cvyKga-f) the curved stuff from old casserole dishes is great for tremolo grinding if you angle it just right. and the really thin, almost paper-like fragments you get from busted picture frames? absolute chaos. they flutter on the mic like evil butterflies.\n\nanother tip: don't clean the glass too much. a little grime does wonders for texture. dust adds hiss, dried soda adds stick-slip crackle, and whatever unidentifiable goo lives in alley bins gives you that signature “what-is-that-and-should-I-be-worried” timbre.\n\nalso: use the whole surface. everyone goes for the edges, but the flat plane can do these weird resonant drags if you press it against the mic casing instead of the pickup. gross sounds. terrible. beautiful.\n\nanyway if anyone saw me at that warehouse show: sorry i wasn't at my best. went on a bit of a… let's call it an “ill-advised spiral.” pretty sure it involved pcp, because the last thing i remember is deciding i needed to “follow the true frequency path,” and then boom—five days later i woke up in milwaukee wearing only one boot and a shirt i'm 90% sure wasn't mine. not my proudest moment. please do not do what i did. honestly no one should.\n\n![enormous glacier](https://upload.wikimedia.org/wikipedia/commons/5/55/Glaciar_Lamplugh%2C_Parque_Nacional_Bah%C3%ADa_del_Glaciar%2C_Alaska%2C_Estados_Unidos%2C_2017-08-19%2C_DD_154.jpg)\n\nbut yeah. happy scraping.`,
                    postDate: new Date(2025, 1, 29, 16, 12),
                    textMode: 'markdown',
                    postId: "",
                    threadId: ""
                },
                {
                    authorId: '3f2c52b0-1c1a-4f4e-a3f0-0a5f2cbe88b0',
                    authorHandle: 'ghost_crayon',
                    body: 'GLINT LORD WHAT. i was not prepared for literally any part of this update omg are you okay??? javascript ![spooky!!](https://www.google.com/url?sa=i&url=https%3A%2F%2Fwww.pinterest.com%2Fideas%2Fdancing-skeleton-gif%2F908019841987%2F&psig=AOvVaw0ha8zpFF0GrIyzGFOr-E7Z&ust=1764906227721000&source=images&opi=89978449)',
                    postDate: new Date(2025, 1, 29, 16, 14),
                    textMode: 'markdown',
                    postId: "",
                    threadId: ""
                }
            ]
        }
    },
]

export const getThreads = async (options?: GetThreadsParam): Promise<Array<ThreadSummary>> => {
    const opts = getThreadsOptionsWithDefaults(options)
    await delayMs(1000)
    if(options?.searchText == 'error'){
        throw new Error('asked for error')
    }
    if(options?.searchText){
        return Promise.resolve(sampleThreads
            .filter(thread => thread.title.includes(options.searchText || ''))
            .slice(0, Math.min(opts.count, sampleThreads.length)))

    }
    return Promise.resolve(sampleThreads.slice(0, Math.min(opts.count, sampleThreads.length)))
}

export const getThread = async (options: GetThreadParam): Promise<ThreadData> => {
    const opts = getThreadOptionsWithDefaults(options)
    await delayMs(1000)
    Logger.info(`getThread with options ${JSON.stringify(options)} ${JSON.stringify(opts)}`)
    return Promise.resolve(sampleThreads.filter(thread => thread.id === opts.threadId)[0])
}

export const getPreviewOfPost = async(opts:PostThreadParam): Promise<ThreadData> => {
    const thread = sampleThreads.filter(thread => thread.id === opts.threadId)[0]
    const post : Post = {
        authorId: 'db47e77a-f1f0-4091-81b4-639ea0bea75e',
        authorHandle: 'migf',
        postDate: new Date(),
        body: opts.body,
        postId: "",
        threadId: ""
    }
    return {
        ...thread,
        postList: {
            ...thread.postList,
            posts: thread.postList.posts.concat(post)
        }   
    }
}

export const postToThread = async (opts: PostThreadParam): Promise<ThreadData> => {
    const thread = sampleThreads.filter(thread => thread.id === opts.threadId)[0]
    const post: Post = {
        authorId: 'db47e77a-f1f0-4091-81b4-639ea0bea75e',
        authorHandle: 'migf',
        postDate: new Date(),
        body: opts.body,
        postId: "",
        threadId: ""
    }
    thread.postList.posts.push(post)
    return getThread({threadId: opts.threadId })
}

const getThreadsOptionsWithDefaults = (options?: GetThreadsParam): GetThreadsOptions => {
    const opts = options || {
        count: DEFAULT_THREAD_REQUEST
    }
    const optionsCount = Math.floor(opts.count || 0)
    const count = optionsCount > MAX_THREAD_REQUEST ? MAX_THREAD_REQUEST
        : optionsCount || DEFAULT_THREAD_REQUEST

    return {
        count
    }
}

const getThreadOptionsWithDefaults = (options: GetThreadParam): GetThreadOptions => {
    const opts = {
        count: MAX_POST_REQUEST,
        threadId: options.threadId
    }
    const optionsCount = Math.floor(opts.count || 0)
    const count = optionsCount > MAX_POST_REQUEST ? MAX_POST_REQUEST
        : optionsCount || MAX_POST_REQUEST

    return {
        count,
        threadId: opts.threadId
    }
}